import { useEffect } from "react";

interface PageMetaProps {
  title: string;
  description: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  jsonLd?: Record<string, unknown>;
}

const DEFAULTS = {
  title: "LaunchTactix — Replace Your Entire Growth Team With AI | 70+ Autonomous Modules",
  description:
    "LaunchTactix is an AI business growth platform that replaces your entire growth team with 70+ autonomous AI modules. Automates lead generation, cold outreach, SEO, social media, and deal pipeline — starting free.",
  canonical: "https://launchtactix.com/",
  ogTitle: "LaunchTactix — Your Competitors Are Hiring. You're Deploying.",
  ogDescription:
    "70+ autonomous AI modules run your entire growth operation — leads, outreach, SEO, social media, scheduling, AI agents, and pipeline — 24/7, without a single hire.",
};

function upsertMeta(attr: string, key: string, content: string) {
  let el = document.head.querySelector(
    `meta[${attr}="${key}"]`
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(id: string, data: Record<string, unknown>) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function PageMeta({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  jsonLd,
}: PageMetaProps) {
  const resolvedOgTitle = ogTitle ?? title;
  const resolvedOgDesc = ogDescription ?? description;

  useEffect(() => {
    document.title = title;
    upsertMeta("name", "description", description);
    upsertCanonical(canonical);
    upsertMeta("property", "og:title", resolvedOgTitle);
    upsertMeta("property", "og:description", resolvedOgDesc);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("name", "twitter:title", resolvedOgTitle);
    upsertMeta("name", "twitter:description", resolvedOgDesc);
    if (jsonLd) upsertJsonLd("page-jsonld", jsonLd);

    return () => {
      document.title = DEFAULTS.title;
      upsertMeta("name", "description", DEFAULTS.description);
      upsertCanonical(DEFAULTS.canonical);
      upsertMeta("property", "og:title", DEFAULTS.ogTitle);
      upsertMeta("property", "og:description", DEFAULTS.ogDescription);
      upsertMeta("property", "og:url", DEFAULTS.canonical);
      upsertMeta("name", "twitter:title", DEFAULTS.ogTitle);
      upsertMeta("name", "twitter:description", DEFAULTS.ogDescription);
      document.getElementById("page-jsonld")?.remove();
    };
  }, [title, description, canonical, resolvedOgTitle, resolvedOgDesc, jsonLd]);

  return null;
}
